// API の呼び出し。Firebase の ID トークンで認証し、書き込みには二重送信防止のキーを付ける
import type { ApiError } from '@torapon/shared';
import { idToken } from './firebase';

export class HttpError extends Error {
  constructor(public status: number, public code: string, message: string, public detail?: unknown) { super(message); }
}

type Opts = { /** 版番号（同時更新の防止） */ ifMatch?: number };

async function call<T>(method: string, path: string, body?: unknown, o: Opts = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (o.ifMatch !== undefined) headers['if-match'] = String(o.ifMatch);
  const token = await idToken();
  if (token) headers.authorization = `Bearer ${token}`;
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') headers['idempotency-key'] = crypto.randomUUID();
  const r = await fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  const data = r.headers.get('content-type')?.includes('json') ? await r.json() : null;
  if (!r.ok) {
    const e = (data as ApiError | null)?.error;
    throw new HttpError(r.status, e?.code ?? 'INTERNAL', e?.message ?? '通信に失敗しました', e?.detail);
  }
  return data as T;
}

/** 写真など、JSON 以外の受け取り。キャッシュには残さない */
async function blob(path: string): Promise<Blob> {
  const token = await idToken();
  const r = await fetch(path, { headers: token ? { authorization: `Bearer ${token}` } : {}, cache: 'no-store' });
  if (!r.ok) throw new HttpError(r.status, 'INTERNAL', '写真を開けませんでした');
  return r.blob();
}

export const api = {
  blob,
  get: <T>(p: string) => call<T>('GET', p),
  post: <T>(p: string, b: unknown, o?: Opts) => call<T>('POST', p, b, o),
  del: <T>(p: string, b: unknown = {}) => call<T>('DELETE', p, b),
  patch: <T>(p: string, b: unknown, o?: Opts) => call<T>('PATCH', p, b, o),
};
